/**
 * useCan / <Can> — helpers RBAC côté frontend.
 *
 * Source de vérité : le set de `permissions` retourné par `auth.me` (cf
 * `getMyContextUseCase` côté backend). Le set est rechargé automatiquement
 * par TanStack Query (`staleTime: 60s` sur `useMe`) — pas besoin d'invalider
 * manuellement après un changement de rôle/perm, le cache se rafraîchira
 * naturellement (et l'utilisateur peut forcer via reload).
 *
 * Convention masquage vs disablement (cf wireframes Phase 4) :
 *   - Masquage (composant <Can> + return null) pour les actions secondaires
 *     dans les menus déroulants ("Faire partir le membre").
 *   - Disablement (rendre le bouton mais `disabled` + tooltip) pour les
 *     actions primaires utiles à signaler ("Demandez au Propriétaire").
 *
 * Anti-pattern : NE JAMAIS utiliser `useCan` pour décider si on AFFICHE un
 * menu de navigation entier (sidebar) — déléguer à un check côté backend
 * via un middleware ou une page d'erreur 403, sinon un user qui devine
 * l'URL pourrait charger la page sans la voir dans le menu.
 */

import type { ReactNode } from "react";

import { useMe } from "./use-me.js";

/**
 * Retourne `granted = true` si l'utilisateur a la permission demandée dans
 * le tenant actif. `isLoading = true` tant que `auth.me` n'a pas répondu.
 *
 * Pendant le chargement, on retourne `false` par défaut (fail-closed UX :
 * mieux vaut cacher temporairement un bouton que d'afficher une action
 * que l'user n'a finalement pas le droit de faire).
 */
export function useCan(permission: string): {
  granted: boolean;
  isLoading: boolean;
} {
  const { data, isLoading } = useMe();
  const granted = data?.permissions?.includes(permission) ?? false;
  return { granted, isLoading };
}

/**
 * Retourne `true` si AU MOINS une des permissions est accordée. Utile pour
 * les sections qui regroupent plusieurs actions distinctes (ex: bandeau
 * admin visible si `roles.read` OU `membres.read`).
 */
export function useCanAny(permissions: ReadonlyArray<string>): {
  granted: boolean;
  isLoading: boolean;
} {
  const { data, isLoading } = useMe();
  const userPerms = data?.permissions ?? [];
  const granted = permissions.some((p) => userPerms.includes(p));
  return { granted, isLoading };
}

/**
 * Composant déclaratif qui ne rend ses enfants QUE si la permission est
 * accordée. Pendant le chargement de `auth.me`, ne rend rien (fail-closed).
 *
 * @example
 *   <Can permission="membres.invite">
 *     <button onClick={openInviteModal}>Inviter un membre</button>
 *   </Can>
 *
 *   <Can permission="domaine.update" fallback={<span className="text-aubergine-500">Lecture seule</span>}>
 *     <FormulaireEdition />
 *   </Can>
 */
export function Can({
  permission,
  fallback = null,
  children,
}: {
  permission: string;
  fallback?: ReactNode;
  children: ReactNode;
}) {
  const { granted, isLoading } = useCan(permission);
  if (isLoading) return null;
  return granted ? <>{children}</> : <>{fallback}</>;
}

/** Variante "any" : rend les enfants si AU MOINS une perm est accordée. */
export function CanAny({
  permissions,
  fallback = null,
  children,
}: {
  permissions: ReadonlyArray<string>;
  fallback?: ReactNode;
  children: ReactNode;
}) {
  const { granted, isLoading } = useCanAny(permissions);
  if (isLoading) return null;
  return granted ? <>{children}</> : <>{fallback}</>;
}
